/**
 * This file is part of Discorch.
 * Copyright (C) 2025 Discorch Contributors.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import discordLogo from "@/assets/discord.png";
import { CopyButton } from "@/components/CopyButton";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { COUNTER_RESPONSE_TEMPLATE } from "@/lib/constants";
import { AlertTriangle, Info } from "lucide-react";

export function ResponsePreview() {
  return (
    <section className="space-y-4">
      <div className="space-y-2">
        <h3 className="flex items-center gap-2 text-sm font-medium">
          <span className="text-muted-foreground">03.</span>
          What to Expect Next
        </h3>
        <p className="text-muted-foreground text-sm">
          Discord's new deceptive process. They will try to reject your request
          first.
        </p>
      </div>

      <div className="space-y-4">
        <Card className="border-red-500/20 bg-red-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="text-red-500">
                Step 1: Discord's Automated Rejection
              </span>
            </CardTitle>
            <CardDescription className="text-xs">
              They will send this misleading response
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <img
                src={discordLogo}
                alt="Discord Support"
                className="h-8 w-8 rounded"
              />
              <div>
                <div className="mb-1 text-xs font-medium">
                  Clyde (Discord Support)
                </div>
                <div className="text-muted-foreground space-y-2 text-sm">
                  <p>Hello,</p>
                  <p>
                    Thanks for reaching out. This channel is reserved for
                    individuals who need clarification about our privacy
                    policies or practices, beyond what it is available in our
                    online resources. All other inquiries should be submitted
                    via our online form, <u>here</u>.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-500/20 bg-amber-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Info className="h-4 w-4 text-amber-500" />
              <span className="text-amber-500">
                Step 2: Your Counter-Response (Required)
              </span>
            </CardTitle>
            <CardDescription className="text-xs">
              Reply with this template to get your request properly reviewed
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="bg-muted/50 rounded border p-3">
              <pre className="text-xs whitespace-pre-wrap">
                {COUNTER_RESPONSE_TEMPLATE}
              </pre>
            </div>
            <CopyButton
              text={COUNTER_RESPONSE_TEMPLATE}
              variant="outline"
              size="sm"
              className="w-full"
            />
          </CardContent>
        </Card>

        <Card className="border-green-500/20 bg-green-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <span className="text-green-500">Step 3: Actual Processing</span>
            </CardTitle>
            <CardDescription className="text-xs">
              After you prove you understand the process
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <img
                src={discordLogo}
                alt="Discord Privacy Team"
                className="h-8 w-8 rounded"
              />
              <div>
                <div className="mb-1 text-xs font-medium">
                  Discord Privacy Team
                </div>
                <div className="text-muted-foreground space-y-2 text-sm">
                  <p>Hi there,</p>
                  <p>Thank you for your patience.</p>
                  <p>
                    This is your confirmation email that we have successfully
                    submitted the message IDs that you have provided for
                    deletion. Please allow up to 24 hours for the deletion
                    process to be completed. After 24 hours, you can confirm
                    that your messages are deleted by requesting your data
                    package <u>here</u>.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-muted">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">
              Final: Completion Confirmation
            </CardTitle>
            <CardDescription className="text-xs">
              Once the deletion is actually finished
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <img
                src={discordLogo}
                alt="Discord Privacy Team"
                className="h-8 w-8 rounded"
              />
              <div>
                <div className="mb-1 text-xs font-medium">
                  Discord Privacy Team
                </div>
                <div className="text-muted-foreground space-y-2 text-sm">
                  <p>
                    The deletion process for the message IDs you provided is now
                    complete.
                  </p>
                  <p>Please note:</p>
                  <ul className="list-inside list-disc space-y-1 pl-2">
                    <li>Message IDs from other users were not deleted</li>
                    <li>
                      Incorrect or incomplete message IDs were not processed
                    </li>
                    <li>
                      Messages from servers/groups you're still in were not
                      deleted
                    </li>
                  </ul>
                  <p>
                    If you find additional messages containing personally
                    identifiable information (PII) that you'd like deleted,
                    please submit a new ticket with the relevant details for
                    review.
                  </p>
                  <div className="border-border mt-4 border-t pt-4 text-xs">
                    After December 1, 2024, only CSV format will be accepted for
                    deletion requests.
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-950/30">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-1 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
          <div>
            <h4 className="text-base font-medium text-red-800 dark:text-red-300">
              Important: Discord's Deceptive Process
            </h4>
            <div className="mt-1 space-y-2 text-sm text-red-700 dark:text-red-400">
              <p>
                <strong>
                  Discord deliberately tries to reject valid privacy requests
                </strong>{" "}
                by claiming you're using the wrong form. This is false — you ARE
                using the correct form.
              </p>
              <p>
                You must reply with the counter-response template to prove you
                understand their process and get your request actually reviewed.
                This is their way of making deletion as difficult as possible.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
